import { TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { Button, Descriptions, Divider, Skeleton, Table } from 'antd';
import { useRouteMatch } from 'react-router-dom';
import { useMaterialList } from '@/core/domain/stock/Material/Material.hooks';
import { useIntl } from 'react-intl';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import moment from 'moment';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { useReturnMaterialDetail } from '@/core/domain/stock/MaterialReturn/MaterialReturn.hooks';

function ReturnDetailsPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const { returnMaterialId } = useRouteMatch<{ returnMaterialId: string }>()
    .params;

  const { data: materialListDate } = useMaterialList({
    order: 'DESC',
  });

  const materials = flatInfiniteResult(materialListDate).find(
    (item) => item.id === returnMaterialId,
  );

  const { data: returnData, isLoading: returnLoading } =
    useReturnMaterialDetail(returnMaterialId);

  const columnsReturn = [
    {
      dataIndex: 'workOrderNumber',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.budget',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.description',
      }),
      ellipsis: true,
    },
    useConfigurationColumnsPart({}),
    {
      dataIndex: 'licensePlate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.licensePlate',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'returnDate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.dateReturn',
      }),
      ellipsis: true,
      render: (value: string) => moment(value).format('DD/MM/YYYY'),
    },
    {
      dataIndex: 'returnedQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.returnedQuantity',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
  ];

  function openReport() {
    window.open(
      `${window.location.origin}/privateReports/materialReturn?returnId=${returnMaterialId}`,
      '_blank',
    );
  }

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <Button type="primary" onClick={openReport}>
          Relatório
        </Button>
      }
    >
      <Skeleton active loading={false}>
        <Descriptions
          column={{ xs: 1, sm: 2, md: 3, xl: 4 }}
          layout={'vertical'}
          className="workorder-descriptions"
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label={'Data da entrega'}>
            {moment(returnData?.result?.returnDate).format('DD/MM/YYYY')}
          </Descriptions.Item>

          <Descriptions.Item label={'Empresa'}>
            {materials?.company}
          </Descriptions.Item>

          <Descriptions.Item label={'Devolvido por'}>
            {returnData?.result?.returnerManName}
          </Descriptions.Item>

          <Descriptions.Item label={'Recebido por'}>
            {returnData?.result?.receivedManName}
          </Descriptions.Item>
        </Descriptions>
      </Skeleton>

      <Table
        columns={columnsReturn}
        dataSource={returnData?.result?.itemReturnDetail}
        loading={returnLoading}
        rowKey={(row) => row.workOrderComplementId}
        pagination={false}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/movements/deliveryOfMaterials" />
      </SpaceForButton>
    </TabContentWrapper>
  );
}

export default ReturnDetailsPage;
